// src/components/ExpirationManagementModal.js
import React, { useState, useEffect, useCallback } from "react";
import { getUsersExpiringSoon, getUsersWithExpiration, updateUserExpiration } from "../services/api";
import styles from "../css/shared.module.css";
import {
  FiX,
  FiCalendar,
  FiEdit2,
  FiCheck,
  FiX as FiCancel,
  FiClock,
  FiUser,
  FiMail,
  FiAlertTriangle,
  FiCheckCircle
} from "react-icons/fi";

const ExpirationManagementModal = ({ isOpen, onClose, initialTab = "expiring" }) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("date-asc");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editingUser, setEditingUser] = useState(null);
  const [newExpirationDate, setNewExpirationDate] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);

  // Cargar datos según la pestaña activa
  const loadData = useCallback(async () => {
    if (!isOpen) return;
    
    setIsLoading(true);
    try {
      let response;
      if (activeTab === "expiring") {
        response = await getUsersExpiringSoon();
      } else {
        response = await getUsersWithExpiration();
      }
      
      if (response.success) {
        setUsers(response.data);
        setFilteredUsers(response.data);
      }
    } catch (error) {
      console.error("Error loading users:", error);
    } finally {
      setIsLoading(false);
    }
  }, [isOpen, activeTab]);

  // Efecto para cargar datos cuando cambia la pestaña o se abre el modal
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Efecto para filtrar y ordenar cuando cambian los filtros
  useEffect(() => {
    let filtered = [...users];

    // Filtro por búsqueda (nombre o email)
    if (searchTerm) {
      filtered = filtered.filter(user => 
        user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtro por estado
    if (statusFilter !== "all") {
      if (statusFilter === "expired") {
        filtered = filtered.filter(user => user.isAccessExpired);
      } else if (statusFilter === "active") {
        filtered = filtered.filter(user => !user.isAccessExpired);
      }
    }

    // Ordenamiento
    filtered.sort((a, b) => {
      if (sortBy === "date-asc") {
        const dateA = new Date(a.accessExpirationDate);
        const dateB = new Date(b.accessExpirationDate);
        return dateA - dateB;
      } else if (sortBy === "date-desc") {
        const dateA = new Date(a.accessExpirationDate);
        const dateB = new Date(b.accessExpirationDate);
        return dateB - dateA;
      }
      return 0;
    });

    setFilteredUsers(filtered);
  }, [users, searchTerm, sortBy, statusFilter]);

  // Manejar edición de fecha
  const handleEditDate = (user) => {
    setEditingUser(user._id);
    setNewExpirationDate(user.accessExpirationDate.split('T')[0]); // Formato YYYY-MM-DD
  };

  // Cancelar edición
  const handleCancelEdit = () => {
    setEditingUser(null);
    setNewExpirationDate("");
  };

  // Guardar nueva fecha
  const handleSaveDate = async (userId) => {
    if (!newExpirationDate) return;

    const selectedDate = new Date(newExpirationDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (selectedDate < today) {
      alert("La fecha de expiración no puede ser anterior a la fecha actual");
      return;
    }

    setIsUpdating(true);
    try {
      const response = await updateUserExpiration(userId, { accessExpirationDate: newExpirationDate });
      if (response.success) {
        // Recargar datos
        await loadData();
        setEditingUser(null);
        setNewExpirationDate("");
      } else {
        alert(response.message || "Error al actualizar la fecha");
      }
    } catch (error) {
      console.error("Error updating expiration date:", error);
      alert("Error al actualizar la fecha");
    } finally {
      setIsUpdating(false);
    }
  };

  // Formatear fecha para mostrar
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  // Calcular días restantes
  const getDaysRemaining = (expirationDate) => {
    const today = new Date();
    const expDate = new Date(expirationDate);
    const diffTime = expDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Obtener estado del usuario
  const getUserStatus = (user) => {
    if (user.isAccessExpired) {
      return { text: "Expirado", color: "red", icon: <FiAlertTriangle /> };
    }
    const days = getDaysRemaining(user.accessExpirationDate);
    if (days <= 7) {
      return { text: `Expira en ${days} días`, color: "orange", icon: <FiClock /> };
    }
    return { text: `Expira en ${days} días`, color: "green", icon: <FiCheckCircle /> };
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.expirationModal}>
        <div className={styles.modalHeader}>
          <h2>Gestión de Expiraciones de Acceso</h2>
          <button className={styles.closeButton} onClick={onClose}>
            <FiX />
          </button>
        </div>

        <div className={styles.modalContent}>
          {/* Pestañas */}
          <div className={styles.tabContainer}>
            <button
              className={`${styles.tab} ${activeTab === "expiring" ? styles.activeTab : ""}`}
              onClick={() => setActiveTab("expiring")}
            >
              <FiClock /> Usuarios que Expiran Pronto
            </button>
            <button
              className={`${styles.tab} ${activeTab === "all" ? styles.activeTab : ""}`}
              onClick={() => setActiveTab("all")}
            >
              <FiUser /> Todos los Usuarios
            </button>
          </div>

          {/* Filtros y búsqueda */}
          <div className={styles.filtersContainer}>
            <div className={styles.searchContainer}>
              <input
                type="text"
                placeholder="Buscar por nombre o email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles.searchInput}
              />
            </div>

            <div className={styles.filterGroup}>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className={styles.filterSelect}
              >
                <option value="date-asc">Fecha de Expiración (Ascendente)</option>
                <option value="date-desc">Fecha de Expiración (Descendente)</option>
              </select>

              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className={styles.filterSelect}
              >
                <option value="all">Todos los estados</option>
                <option value="active">Activos</option>
                <option value="expired">Expirados</option>
              </select>
            </div>
          </div>

          {/* Tabla de usuarios */}
          <div className={styles.tableContainer}>
            {isLoading ? (
              <div className={styles.loadingMessage}>Cargando usuarios...</div>
            ) : (
              <table className={styles.expirationTable}>
                <thead>
                  <tr>
                    <th>Usuario</th>
                    <th>Email</th>
                    <th>Fecha de Expiración</th>
                    <th>Días Restantes</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => {
                    const status = getUserStatus(user);
                    const isEditing = editingUser === user._id;
                    
                    return (
                      <tr key={user._id}>
                        <td>
                          <div className={styles.userInfo}>
                            <FiUser className={styles.userIcon} />
                            <span>{user.username}</span>
                          </div>
                        </td>
                        <td>
                          <div className={styles.emailInfo}>
                            <FiMail className={styles.emailIcon} />
                            <span>{user.email}</span>
                          </div>
                        </td>
                        <td>
                          {isEditing ? (
                            <input
                              type="date"
                              value={newExpirationDate}
                              onChange={(e) => setNewExpirationDate(e.target.value)}
                              min={new Date().toISOString().split('T')[0]}
                              className={styles.dateInput}
                            />
                          ) : (
                            <div className={styles.dateInfo}>
                              <FiCalendar className={styles.dateIcon} />
                              <span>{formatDate(user.accessExpirationDate)}</span>
                            </div>
                          )}
                        </td>
                        <td>
                          <span className={`${styles.daysRemaining} ${status.color}`}>
                            {getDaysRemaining(user.accessExpirationDate)}
                          </span>
                        </td>
                        <td>
                          <div className={`${styles.statusBadge} ${status.color}`}>
                            {status.icon}
                            <span>{status.text}</span>
                          </div>
                        </td>
                        <td>
                          {isEditing ? (
                            <div className={styles.editActions}>
                              <button
                                className={styles.saveButton}
                                onClick={() => handleSaveDate(user._id)}
                                disabled={isUpdating}
                              >
                                <FiCheck />
                              </button>
                              <button
                                className={styles.cancelButton}
                                onClick={handleCancelEdit}
                                disabled={isUpdating}
                              >
                                <FiCancel />
                              </button>
                            </div>
                          ) : (
                            <button
                              className={styles.editButton}
                              onClick={() => handleEditDate(user)}
                            >
                              <FiEdit2 />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {filteredUsers.length === 0 && !isLoading && (
            <div className={styles.emptyState}>
              <FiUser className={styles.emptyIcon} />
              <p>No se encontraron usuarios</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExpirationManagementModal;
